import React from "react";
import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { Link } from "react-scroll";
import CloudIcon from "@mui/icons-material/Cloud";
import SpeedIcon from "@mui/icons-material/Speed";
import SecurityIcon from "@mui/icons-material/Security";
import AnimatedBackground from "./AnimatedBackground";

const Hero: React.FC = () => {
  return (
    <Box
      id="hero"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <AnimatedBackground />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, pt: 10 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography
              variant="h1"
              sx={{
                mb: 3,
                fontWeight: 800,
                fontSize: { xs: "2.5rem", md: "4rem" },
                background: "linear-gradient(135deg, #ffffff 0%, #00a8ff 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Fast & Secure
              <br />
              Hosted Solutions
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                color: "text.secondary",
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
              Intelligent Signal Solutions LLC is a trusted hosted solutions
              provider with over 30 years of combined civilian and government
              service experience.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Link to="services" smooth={true} duration={500}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    background:
                      "linear-gradient(135deg, #00a8ff 0%, #0066cc 100%)",
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5dcdff 0%, #3385db 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 24px rgba(0, 168, 255, 0.4)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Explore Services
                </Button>
              </Link>
              <Link to="contact" smooth={true} duration={500}>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: "primary.main",
                    color: "primary.main",
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    "&:hover": {
                      borderColor: "primary.light",
                      bgcolor: "rgba(0, 168, 255, 0.1)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Contact Us
                </Button>
              </Link>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {[
                {
                  icon: <SpeedIcon />,
                  title: "High Performance",
                  description: "Lightning-fast infrastructure",
                },
                {
                  icon: <SecurityIcon />,
                  title: "Secure",
                  description: "Enterprise-grade security",
                },
                {
                  icon: <CloudIcon />,
                  title: "Reliable",
                  description: "99.9% uptime guarantee",
                },
              ].map((feature, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 3,
                    bgcolor: "rgba(20, 27, 61, 0.6)",
                    borderRadius: 2,
                    border: "1px solid rgba(0, 168, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateX(10px)",
                      borderColor: "primary.main",
                      boxShadow: "0 8px 24px rgba(0, 168, 255, 0.2)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 56,
                      height: 56,
                      borderRadius: "12px",
                      background:
                        "linear-gradient(135deg, #00a8ff 0%, #0066cc 100%)",
                      flexShrink: 0,
                    }}
                  >
                    {React.cloneElement(feature.icon, { sx: { fontSize: 32 } })}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;
